'use client';

import { useRouter } from 'next/navigation';
import { ResumeIntakeDialog } from './ResumeIntakeDialog';
import type {
  IntakeHandlerResult,
  IntakeProgressReporter,
  IntakeResume,
} from './ResumeIntakePanel';

interface ResumeCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (resumeId: string) => void;
  /**
   * When true, the dialog skips navigation after a successful create/upload
   * and only fires `onCreated`. Used by ResumeSelector (outreach flows).
   */
  disableNavigation?: boolean;
}

/**
 * Thin wrapper around the unified ResumeIntakeDialog. Preserves the historical
 * "library" behavior: clicking Create routes to the builder, uploading stays
 * on the current page, and selecting an existing resume opens its view URL.
 */
export function ResumeCreateDialog({
  open,
  onOpenChange,
  onCreated,
  disableNavigation = false,
}: ResumeCreateDialogProps) {
  const router = useRouter();

  const handleCreate = async (
    name: string,
    progress: IntakeProgressReporter,
  ): Promise<IntakeHandlerResult> => {
    progress.setStages([
      { key: 'create', label: 'Creating resume…' },
      { key: 'open', label: disableNavigation ? 'Finalizing…' : 'Opening builder…' },
    ]);
    const response = await fetch('/api/resume-builder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const payload = (await response.json().catch(() => null)) as
      | { resume?: { id?: string }; error?: string }
      | null;
    const resumeId = payload?.resume?.id;
    if (!response.ok || !resumeId) {
      throw new Error(payload?.error || 'Failed to create resume');
    }
    progress.setStage('open');
    onCreated?.(resumeId);
    if (!disableNavigation) {
      router.push(`/d/pitch/resumes/builder/${resumeId}`);
    }
    return { resumeId, shouldClose: true };
  };

  const handleUpload = async (
    file: File,
    progress: IntakeProgressReporter,
  ): Promise<IntakeHandlerResult> => {
    progress.setStages([
      { key: 'upload', label: 'Uploading file…' },
      { key: 'parse', label: 'Parsing resume content…' },
    ]);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    const response = await fetch('/api/settings/resumes', {
      method: 'POST',
      body: formData,
    });
    progress.setStage('parse');
    const payload = (await response.json().catch(() => null)) as
      | { success?: boolean; resume?: { id?: string }; error?: string }
      | null;
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || 'Failed to upload resume');
    }
    const resumeId = payload.resume?.id;
    onCreated?.(resumeId ?? '');
    if (!disableNavigation) {
      router.push('/d/pitch/resumes');
    }
    return { resumeId, shouldClose: true };
  };

  const handleSelectExisting = async (
    resume: IntakeResume,
    progress: IntakeProgressReporter,
  ): Promise<IntakeHandlerResult> => {
    progress.setStages([{ key: 'open', label: 'Opening resume…' }]);
    if (disableNavigation) {
      onCreated?.(resume.id);
    } else {
      router.push(resume.viewUrl || '/d/pitch/resumes');
    }
    return { resumeId: resume.id, shouldClose: true };
  };

  return (
    <ResumeIntakeDialog
      open={open}
      onOpenChange={onOpenChange}
      onCreate={handleCreate}
      onUpload={handleUpload}
      onSelectExisting={handleSelectExisting}
    />
  );
}
